import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowLeft, ArrowRight } from "lucide-react";
import { FAQS } from "../data/content";

export const FaqModal = ({ activeIndex, onClose, onNavigate }) => {
    const faq = activeIndex !== null ? FAQS[activeIndex] : null;

    useEffect(() => {
        if (activeIndex === null) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft") onNavigate((activeIndex + FAQS.length - 1) % FAQS.length);
            if (e.key === "ArrowRight") onNavigate((activeIndex + 1) % FAQS.length);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [activeIndex, onClose, onNavigate]);

    return (
        <AnimatePresence>
            {faq && (
                <motion.div
                    data-testid="faq-modal-overlay"
                    className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 backdrop-blur-md p-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    <motion.div
                        key={faq.id}
                        data-testid="faq-modal-content"
                        className="relative w-full max-w-2xl rounded-2xl border border-slate-200 bg-white p-6 sm:p-10 shadow-2xl"
                        initial={{ opacity: 0, scale: 0.94, y: 16 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: 10 }}
                        transition={{ type: "spring", stiffness: 300, damping: 26 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <span className="font-mono2 text-xs font-semibold tracking-widest text-teal-700 bg-teal-50 border border-teal-200/70 rounded-full px-3 py-1">
                                    FAQ {faq.id} / 10
                                </span>
                                <span className="hidden sm:inline font-mono2 text-xs uppercase tracking-widest text-slate-400">
                                    {faq.tag}
                                </span>
                            </div>
                            <button
                                data-testid="faq-modal-close-button"
                                onClick={onClose}
                                aria-label="Close"
                                className="rounded-full p-2 text-slate-400 hover:text-slate-800 hover:bg-slate-100 transition-colors"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <h3 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 leading-snug mb-4">
                            {faq.question}
                        </h3>
                        <p className="font-body text-sm sm:text-base text-slate-600 leading-relaxed">
                            {faq.answer}
                        </p>

                        <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-5">
                            <button
                                data-testid="faq-modal-prev-button"
                                onClick={() => onNavigate((activeIndex + FAQS.length - 1) % FAQS.length)}
                                className="group flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 font-mono2 text-xs uppercase tracking-widest text-slate-500 transition-all duration-200 hover:border-teal-500 hover:text-teal-700"
                            >
                                <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-0.5" />
                                Prev
                            </button>
                            <span className="hidden md:block font-mono2 text-[10px] uppercase tracking-widest text-slate-300">
                                ← → navigate · esc close
                            </span>
                            <button
                                data-testid="faq-modal-next-button"
                                onClick={() => onNavigate((activeIndex + 1) % FAQS.length)}
                                className="group flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 font-mono2 text-xs uppercase tracking-widest text-slate-500 transition-all duration-200 hover:border-teal-500 hover:text-teal-700"
                            >
                                Next
                                <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
